import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { ParkingsList } from './ParkingsList.tsx';
import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../../shared-mui-components/HeaderWithButtons.tsx';
import { LocalParking } from '@mui/icons-material';
import { Box } from '@mui/material';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../../ui/LastUpdateTimestampAndRefresh.tsx';
import {
    OperatorParking,
    useParkingsFetch,
} from '../../common/state/useParkingsFetch.ts';
import { ParkingsMap } from './ParkingsMap.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import { ServerRequestState } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ToggleGroup } from '../../shared-mui-components/ToggleGroup.tsx';

export const enum ParkingsTab {
    MAP = 'MAP',
    LIST = 'LIST',
}

export function Parkings({ tab }: { tab: ParkingsTab }) {
    const navigate = useNavigate();

    const [parkingsState, refetchParkings] = useParkingsFetch();

    const { storeState } = useStore(store => ({
        currentLogin: CurrentOperatorLoginState.get(store),
    }));

    if (!storeState.currentLogin.data) {
        return null;
    }

    return (
        <>
            <MainContent>
                <HeaderWithButtons
                    title={
                        <Localized
                            de="Parkings"
                            fr="Parkings"
                            it="Parcheggi"
                            en="Parkings"
                        />
                    }
                    icon={LocalParking}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingX: 3,
                        height: 55,
                        py: 1,
                        backgroundColor: 'blue.light',
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            maxWidth: '33%',
                        }}
                    >
                        <ToggleGroup
                            value={tab}
                            onChange={d => {
                                switch (d) {
                                    case ParkingsTab.MAP:
                                        navigate('..');
                                        break;
                                    case ParkingsTab.LIST:
                                        navigate('list');
                                        break;
                                }
                            }}
                            valueRenders={[
                                {
                                    value: ParkingsTab.LIST,
                                    description: (
                                        <Localized
                                            de="Parkingliste"
                                            fr="Liste des parkings"
                                            it="Lista parcheggi"
                                            en="Parkings list"
                                        />
                                    ),
                                },
                                {
                                    value: ParkingsTab.MAP,
                                    description: (
                                        <Localized
                                            de="Karte"
                                            fr="Plan"
                                            it="Mappa"
                                            en="Map"
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Box>
                    <LastUpdateTimestampAndRefreshAsyncLoadedSection
                        requestState={parkingsState}
                        onRefresh={refetchParkings}
                        render={(list, timestamp) => (
                            <LastUpdateTimestampAndRefresh
                                resultsCount={
                                    list.filter(isActiveParking).length
                                }
                                eof={true}
                                onRefresh={refetchParkings}
                                timestamp={timestamp}
                            />
                        )}
                    />
                </Box>
                <OperatorAsyncLoadedSection
                    requestState={parkingsState}
                    pendingLoaderPreset={
                        LoadingSpinnerPresets.FillAllSpaceAndCenter
                    }
                    render={parkings => {
                        const activeParkings = parkings.filter(isActiveParking);

                        if (activeParkings.length < 1) {
                            return <EmptyResults />;
                        }

                        switch (tab) {
                            case ParkingsTab.MAP:
                                return (
                                    <ParkingsMap parkings={activeParkings} />
                                );
                            case ParkingsTab.LIST:
                                return (
                                    <ParkingsList parkings={activeParkings} />
                                );
                        }
                    }}
                />
            </MainContent>
            <ParkingsOutlet
                parkingsState={parkingsState}
                refetchParkings={refetchParkings}
            />
        </>
    );
}

function ParkingsOutlet({
    parkingsState,
    refetchParkings,
}: ParkingsOutletContext) {
    return <Outlet context={{ parkingsState, refetchParkings }} />;
}

type ParkingsOutletContext = {
    parkingsState: ServerRequestState<OperatorParking[], unknown>;
    refetchParkings: () => void;
};

export function useParkingsOutletContext() {
    return useOutletContext<ParkingsOutletContext>();
}

function isActiveParking(parking: OperatorParking) {
    return parking.isOperatorStateActive;
}
